import React, { useState } from "react";
import {
  TrendingUp,
  ShieldCheck,
  Zap,
  ArrowRight,
  CheckCircle2,
  Building2,
  Clock,
  Sparkles,
  ChevronRight,
  Download,
  Award,
  Layers,
} from "lucide-react";

export default function CaseStudiesPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Core metrics matching your banner content
  const heroMetrics = [
    {
      icon: TrendingUp,
      stat: "40%",
      label: "Increase in Efficiency",
    },
    {
      icon: ShieldCheck,
      stat: "60%",
      label: "Reduction in Costs",
    },
    {
      icon: Zap,
      stat: "3X",
      label: "Faster Time to Market",
    },
  ];

  // Detailed Case Studies dataset
  const caseStudies = [
    {
      id: "fintech",
      tag: "FinTech Automation",
      client: "Global Banking Group",
      title: "Automating Risk Analysis & Compliance Reporting with GenAI",
      impact: "Reduced loan approval cycles from 48 hours to under 15 minutes.",
      challenge:
        "Manual risk processing caused bottlenecks, human error, and compliance overhead across 50,000+ monthly loan applications.",
      solution:
        "Deployed an end-to-end LLM-powered document intelligence system integrated directly into legacy banking ERP pipelines.",
      results: [
        "40% overall boost in operational efficiency",
        "60% reduction in processing costs per application",
        "Zero compliance errors across 12 consecutive months",
      ],
      timeline: "12 Weeks Delivery",
    },
    {
      id: "healthcare",
      tag: "HealthTech AI",
      client: "MediCare Systems",
      title: "AI-Powered Patient Diagnostics & Electronic Health Records",
      impact: "Processed millions of health records with 99.4% diagnostic accuracy.",
      challenge:
        "Doctors spent over 4 hours daily on administrative EHR documentation rather than direct patient care.",
      solution:
        "Built HIPAA-compliant speech-to-text ambient clinical scribes and real-time diagnostic suggestion models.",
      results: [
        "3.5x faster diagnostic report turnarounds",
        "55% reduction in administrative overhead",
        "98% physician adoption rate in 30 days",
      ],
      timeline: "8 Weeks Delivery",
    },
    {
      id: "logistics",
      tag: "Supply Chain",
      client: "LogiTrans International",
      title: "Predictive Route Optimization & Automated Fleet Logistics",
      impact: "Saved over 1.2M gallons of fuel annually across 4,000 fleet vehicles.",
      challenge:
        "Unpredictable traffic patterns and manual dispatching led to high fuel expenditure and missed delivery SLAs.",
      solution:
        "Engineered real-time dynamic route optimization engines connected directly to IoT telematics.",
      results: [
        "3X faster delivery dispatch times",
        "28% drop in annual fuel consumption",
        "99.1% on-time delivery rate",
      ],
      timeline: "16 Weeks Delivery",
    },
  ];

  const activeStudy = caseStudies[activeTab];

  return (
    <div className="bg-slate-50 text-slate-800 font-sans min-h-screen overflow-x-hidden selection:bg-[#f25c05] selection:text-white">
      
      {/* ========================================================================= */}
      {/* SECTION 1: HERO IMPACT BANNER (Dark Backdrop + Orange Metrics)             */}
      {/* ========================================================================= */}
      <section className="relative bg-slate-950 text-white py-16 sm:py-24 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-slate-800/80 overflow-hidden">
        
        {/* Subtle Ambient Orange Glow */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#f25c05]/15 rounded-full blur-[140px] pointer-events-none -translate-y-1/2" />

        {/* Dynamic Wave Vector Graphics */}
        <div className="absolute right-0 bottom-0 top-0 w-full lg:w-1/2 opacity-20 pointer-events-none overflow-hidden">
          <svg
            className="w-full h-full text-[#f25c05]"
            viewBox="0 0 600 600"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 450 C 150 400, 300 550, 600 350 C 750 250, 450 150, 600 0"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M0 500 C 200 420, 250 580, 600 400 C 700 300, 500 200, 600 50"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M0 550 C 250 440, 200 600, 600 450 C 650 350, 550 250, 600 100"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Headline Column */}
            <div className="lg:col-span-5 space-y-5 sm:space-y-6 text-left">
              <span className="text-[#f25c05] text-xs sm:text-sm font-extrabold uppercase tracking-widest block">
                CASE STUDY
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-[1.15] tracking-tight">
                Transforming Businesses With AI-Driven Innovation
              </h1>

              <p className="text-slate-400 text-sm sm:text-base md:text-lg leading-relaxed font-normal">
                Discover how we helped our clients automate operations, reduce costs and achieve scalable growth.
              </p>

              <div className="pt-2 sm:pt-4">
                <a
                  href="#featured-studies"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-3.5 px-7 rounded-xl transition-all duration-200 shadow-lg shadow-[#f25c05]/25 hover:shadow-xl hover:shadow-[#f25c05]/40 text-sm sm:text-base"
                >
                  View Case Study
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>

            {/* Right Metrics Grid */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x sm:divide-slate-800">
                {heroMetrics.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={idx}
                      className="flex flex-col items-start sm:items-center text-left sm:text-center px-0 sm:px-6 py-4 sm:py-0 first:pl-0 last:pr-0"
                    >
                      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-800 text-[#f25c05] mb-4 shadow-inner">
                        <Icon size={26} />
                      </div>
                      <span className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
                        {item.stat}
                      </span>
                      <span className="mt-2 text-xs sm:text-sm font-semibold text-slate-400 leading-snug">
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 2: LIGHT-THEMED INTERACTIVE CASE STUDY SPOTLIGHT                 */}
      {/* ========================================================================= */}
      <section id="featured-studies" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-[#f25c05] text-xs font-bold uppercase tracking-wider">
            <Sparkles size={14} />
            Proven Real-World Impact
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Featured Enterprise Implementations
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            Select an industry sector below to examine engineering frameworks and performance metrics.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {caseStudies.map((study, idx) => (
            <button
              key={study.id}
              onClick={() => setActiveTab(idx)}
              className={`px-5 py-3 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer flex items-center gap-2 border ${
                activeTab === idx
                  ? "bg-[#f25c05] text-white border-[#f25c05] shadow-lg shadow-[#f25c05]/20 active:scale-95"
                  : "bg-white text-slate-700 border-slate-200 hover:border-orange-300 hover:text-[#f25c05] hover:bg-orange-50/50"
              }`}
            >
              <Building2 size={16} />
              {study.client}
            </button>
          ))}
        </div>

        {/* Dynamic Detail Card */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-xl shadow-slate-200/60 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column: Brief & Strategy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3.5 py-1 bg-orange-100/70 text-[#f25c05] text-xs font-extrabold rounded-lg border border-orange-200">
                  {activeStudy.tag}
                </span>
                <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                  <Clock size={14} className="text-slate-400" />
                  {activeStudy.timeline}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-snug">
                {activeStudy.title}
              </h3>

              {/* Challenge Container */}
              <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200/60 space-y-1.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Layers size={14} className="text-[#f25c05]" />
                  The Business Challenge:
                </h4>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  {activeStudy.challenge}
                </p>
              </div>

              {/* Solution Container */}
              <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200/60 space-y-1.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Award size={14} className="text-[#f25c05]" />
                  Our Engineering Solution:
                </h4>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  {activeStudy.solution}
                </p>
              </div>
            </div>

            {/* Right Dark Sidebar: Key Results */}
            <div className="lg:col-span-5 bg-slate-950 text-white p-6 sm:p-8 rounded-2xl border border-slate-900 flex flex-col justify-between space-y-6 shadow-2xl relative overflow-hidden">
              
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#f25c05]/10 rounded-full blur-2xl pointer-events-none" />

              <div>
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#f25c05] mb-5 flex items-center gap-2">
                  <TrendingUp size={16} />
                  Quantifiable Results
                </h4>

                <div className="space-y-4">
                  {activeStudy.results.map((res, i) => (
                    <div key={i} className="flex items-start gap-3 text-slate-200 text-sm sm:text-base">
                      <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
                      <span className="font-semibold leading-relaxed">{res}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-850">
                <button className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-slate-200 font-bold py-3.5 px-4 rounded-xl border border-slate-800 transition active:scale-95 text-xs sm:text-sm cursor-pointer">
                  <Download size={16} className="text-[#f25c05]" />
                  Download Full PDF Architecture
                </button>
              </div>

            </div>

          </div>
        </div>

      </section>

      {/* ========================================================================= */}
      {/* SECTION 3: DARK CALL TO ACTION (CTA) FOOTER BANNER                      */}
      {/* ========================================================================= */}
      <section className="bg-slate-950 text-white py-16 sm:py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-900 relative overflow-hidden">
        
        {/* Glow Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] sm:w-[600px] h-[180px] bg-[#f25c05]/15 rounded-full blur-[110px] pointer-events-none" />

        <div className="max-w-5xl mx-auto bg-slate-900/90 border border-slate-800 p-8 sm:p-12 rounded-3xl shadow-2xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center text-center lg:text-left">
            
            <div className="lg:col-span-8 space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-[#f25c05]">
                Start Your Transformation
              </span>
              <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-snug">
                Ready to achieve similar AI-driven results for your organization?
              </h3>
              <p className="text-slate-400 text-sm sm:text-base">
                Book a direct technical session with our solution architects to evaluate your workflow bottlenecks.
              </p>
            </div>

            <div className="lg:col-span-4 flex justify-center lg:justify-end">
              <a
                href="/contact"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-4 px-8 rounded-xl transition duration-200 shadow-xl shadow-[#f25c05]/20 text-sm sm:text-base whitespace-nowrap"
              >
                Schedule AI Audit
                <ChevronRight size={18} />
              </a>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}